/**
 * useRealtimeSession — state machine for the realtime roleplay session.
 *
 * Receives semantic protocol events from the WebSocket consumer and manages:
 *   - Session phase (setup → active → ended)
 *   - User-facing status text
 *   - Streaming transcripts (assistant + user)
 *   - Marble/orb state derivation
 *   - Thinking escalation (slow / very slow)
 *   - Interruption acknowledgement
 *   - Reconnection-aware setup text
 *
 * The consumer translates raw OpenAI events into a clean protocol
 * (setup_status, session_state, audio_delta, transcript, error, etc.)
 * so this composable never sees OpenAI event types.
 */
import { computed, type ComputedRef, type Ref, ref } from "vue";

// ─────────────────────────────────────────────────────────────────────────────
// Configuration — all thresholds, timings, and UX messages in one place.
// Tune these without hunting through component code.
// ─────────────────────────────────────────────────────────────────────────────

// All values here are initial guesses subject to tuning after real-user testing.
// Changing a value here is all that's needed — no other code references magic numbers.
export const REALTIME_SESSION_CONFIG = {
    // ── Timing thresholds (ms) ───────────────────────────────────────────
    timing: {
        /** How long "Your turn" text stays visible before fading */
        listeningTextFadeMs: 5_000,
        /** How long assistant transcript lingers after Nadia finishes */
        assistantTranscriptFadeMs: 2_000,
        /** How long user transcript lingers after turn ends */
        userTranscriptFadeMs: 1_500,
        /** "Connected — resuming session" toast duration */
        reconnectedToastMs: 4_000,
        /** After this long in "thinking", escalate the message.
         *  Median TTFAB is ~950ms, so 2s catches the long tail without
         *  triggering on normal turns. Adjust if TTFAB distribution shifts. */
        slowThinkingThresholdMs: 2_000,
        /** Second escalation — user has been waiting 5s+ with no audio. */
        verySlowThinkingThresholdMs: 5_000,
        /** How long the interrupted dim treatment stays visible before hiding */
        interruptionAckMs: 600,
    },

    // ── Reconnection ─────────────────────────────────────────────────────
    reconnection: {
        /** Maximum reconnection attempts before giving up */
        maxAttempts: 5,
        /** Base interval between reconnection attempts (ms) */
        baseIntervalMs: 2_000,
        /** Multiplier for exponential backoff (attempt N → baseInterval * backoff^(N-1)) */
        backoffMultiplier: 1.5,
        /** Cap on backoff interval (ms) */
        maxIntervalMs: 15_000,
        /** Number of disconnects in the stability window to trigger "unstable" warning */
        instabilityDisconnectThreshold: 3,
        /** Rolling window (ms) in which disconnects are counted for instability */
        instabilityWindowMs: 5 * 60_000,
    },

    // ── Status text: Setup phase (first connect) ─────────────────────────
    setupText: {
        connecting: "Setting up your session…",
        configuring: "Configuring your coach…",
        preparing: "Preparing your session…",
        loading_context: "Loading your conversation…",
        replaying_history: "Restoring your conversation…",
        starting: "Nadia is about to speak…",
    } as Record<string, string>,

    // ── Status text: Setup phase AFTER a reconnect ───────────────────────
    reconnectSetupText: {
        connecting: "Reconnecting…",
        configuring: "Re-establishing your session…",
        preparing: "Reconnecting to your coach…",
        loading_context: "Reloading your conversation…",
        replaying_history: "Restoring your conversation…",
        starting: "Nadia is about to speak…",
    } as Record<string, string>,

    // ── Status text: Active session ──────────────────────────────────────
    // processing_turn maps to the gap between semantic VAD deciding the user
    // is done and response.created firing — typically ~100ms but can spike.
    activeText: {
        speaking: "",
        listening: "Your turn — speak whenever you're ready",
        user_speaking: "Listening…",
        processing_turn: "Finishing listening to your turn…",
        thinking: "Nadia is thinking…",
    } as Record<string, string>,

    // ── Status text: Slow/degraded states ────────────────────────────────
    degradedText: {
        slowThinking: "This is taking a bit longer than usual…",
        verySlowThinking: "Still working on it — hang tight…",
        connectionUnstable: "Your connection seems unstable",
    },

    // ── Status text: Reconnection ────────────────────────────────────────
    reconnectionText: {
        trying: "Trying to reconnect…",
        tryingWithAttempt: "Trying to reconnect (attempt {n})…",
        tryingCountdown: "Retrying in {seconds}s…",
        preserved: "Your conversation will be preserved.",
        gaveUp: "Unable to reconnect",
        gaveUpDetail: "Your conversation has been saved. You can rejoin or view your recap.",
        reconnected: "Connected — resuming session",
    },

    // ── Status text: Errors ──────────────────────────────────────────────
    errorText: {
        setupTimeout: "Taking longer than expected — retrying…",
        setupFailed: "Session configuration error. Please try again.",
        connectionError: "Connection error. Please try again.",
        micPermissionLost: "Microphone access was lost. Please check your browser permissions.",
        micNotFound: "No microphone detected.",
        micPermissionDenied: "Microphone access denied. Enable it in your browser settings to continue.",
        genericError: "Something went wrong. Please try again.",
    },

    // ── User-tunable session settings ────────────────────────────────────
    // These map user-friendly labels to OpenAI Realtime API parameters.
    // All are updatable mid-session via session.update (no reconnect needed).
    // Full API reference: https://developers.openai.com/api/docs/guides/realtime
    //
    // The consumer validates incoming values against ALLOWED_SETTINGS before
    // forwarding to OpenAI, so the frontend only needs to send the user-facing
    // key names defined here.
    userSettings: {
        /** Conversation pace → turn_detection.eagerness (semantic_vad mode).
         *  Controls how long the model waits before deciding the user is done talking.
         *  Lower eagerness = more patience = fewer false interruptions.
         *  See: https://developers.openai.com/api/docs/guides/realtime-vad */
        pace: {
            default: "medium" as "low" | "medium" | "high",
            options: [
                { value: "low", label: "Patient", description: "Waits longer before responding. Try this if Nadia cuts in too early or you need time to think." },
                { value: "medium", label: "Balanced", description: "Default — responds when you seem done speaking." },
                { value: "high", label: "Quick", description: "Responds faster. Best for rapid back-and-forth, but may jump in early." },
            ],
        },

        /** Microphone type → noise_reduction.type.
         *  Filters audio before VAD and the model to reduce false triggers.
         *  "near_field" = headset/earbuds (lighter filtering).
         *  "far_field" = laptop/room mic (aggressive filtering).
         *  "off" = no filtering (quiet environment).
         *  See: https://platform.openai.com/docs/api-reference/realtime-sessions */
        microphone: {
            default: "far_field" as "near_field" | "far_field" | "off",
            options: [
                { value: "far_field", label: "Laptop / room mic", description: "Strong filtering. Use if Nadia hears too much." },
                { value: "near_field", label: "Headset / earbuds", description: "Light filtering. Use with headphones or a close mic." },
                { value: "off", label: "No filtering", description: "No filtering. Best in quiet environments." },
            ],
        },

        /** Allow interruptions → turn_detection.interrupt_response.
         *  When true, speaking while the assistant talks cancels her response.
         *  When false, the assistant always finishes before the user's turn starts.
         *  Disabling is useful when background noise causes frequent false barge-ins. */
        allowInterruptions: {
            default: true,
            label: "Interrupt mode",
            description: "When on, you can cut in mid-sentence. When off, Nadia always finishes her thought.",
        },
    },
} as const;

// Shorthand
const CONFIG = REALTIME_SESSION_CONFIG;

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type SetupStep = "connecting" | "configuring" | "preparing" | "loading_context" | "replaying_history" | "starting";

type ActivePhase = "speaking" | "listening" | "user_speaking" | "processing_turn" | "thinking" | "interrupted";

export type SessionPhase =
    | { stage: "setup"; step: SetupStep; detail?: string }
    | { stage: "active"; phase: ActivePhase }
    | { stage: "muted" }
    | { stage: "reconnecting"; attempts: number }
    | { stage: "reconnect_failed" }
    | { stage: "ended"; reason: string }
    | { stage: "error"; message: string };

export type MarbleState = "setup" | "listening" | "speaking" | "user_speaking" | "thinking" | "muted" | "reconnecting";

type ThinkingEscalation = "normal" | "slow" | "very_slow";

export interface TranscriptEntry {
    text: string;
    final: boolean;
    visible: boolean;
    /** True when the transcript was cut off by a user interruption */
    interrupted: boolean;
}

export interface Transcripts {
    assistant: TranscriptEntry;
    user: TranscriptEntry;
}

// Protocol event types from the server
interface SetupStatusEvent {
    type: "setup_status";
    step: SetupStep;
    detail?: string;
}

interface SessionStateEvent {
    type: "session_state";
    state: ActivePhase;
}

interface AudioDeltaEvent {
    type: "audio_delta";
    delta: string;
    response_id: string;
}

interface TranscriptEvent {
    type: "transcript";
    role: "assistant" | "user";
    text: string;
    final: boolean;
}

interface SessionReconnectedEvent {
    type: "session_reconnected";
    replayed_messages: number;
}

interface MaxDurationEvent {
    type: "max_session_duration_reached";
    message?: string;
}

interface ErrorEvent {
    type: "error";
    message: string;
}

interface SettingsUpdatedEvent {
    type: "settings_updated";
    settings: Partial<UserSessionSettings>;
}

type ProtocolEvent = SetupStatusEvent | SessionStateEvent | AudioDeltaEvent | TranscriptEvent | SessionReconnectedEvent | MaxDurationEvent | ErrorEvent | SettingsUpdatedEvent;

/** User-tunable settings sent to the server via { type: "update_settings" }. */
export interface UserSessionSettings {
    pace: "low" | "medium" | "high";
    microphone: "near_field" | "far_field" | "off";
    allowInterruptions: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Composable
// ─────────────────────────────────────────────────────────────────────────────

function makeEmptyTranscript(): TranscriptEntry {
    return { text: "", final: false, visible: false, interrupted: false };
}

export function useRealtimeSession() {
    const phase: Ref<SessionPhase> = ref({ stage: "setup", step: "connecting" });
    const transcripts: Ref<Transcripts> = ref({
        assistant: makeEmptyTranscript(),
        user: makeEmptyTranscript(),
    });

    // ── Internal state ───────────────────────────────────────────────────

    // Current user-tunable settings (tracks confirmed server-side values)
    const currentSettings: Ref<UserSessionSettings> = ref({
        pace: CONFIG.userSettings.pace.default,
        microphone: CONFIG.userSettings.microphone.default,
        allowInterruptions: CONFIG.userSettings.allowInterruptions.default,
    });

    // Timers for fade-out behavior
    let assistantFadeTimer: ReturnType<typeof setTimeout> | null = null;
    let userFadeTimer: ReturnType<typeof setTimeout> | null = null;
    let listeningFadeTimer: ReturnType<typeof setTimeout> | null = null;

    // Thinking escalation timers
    let slowThinkingTimer: ReturnType<typeof setTimeout> | null = null;
    let verySlowThinkingTimer: ReturnType<typeof setTimeout> | null = null;

    // Track whether "Your turn" text should be visible (fades after listeningTextFadeMs)
    const showListeningText: Ref<boolean> = ref(false);

    // Thinking escalation level
    const thinkingEscalation: Ref<ThinkingEscalation> = ref("normal");

    // Whether we're in a reconnection cycle (selects reconnectSetupText vs setupText)
    const isReconnecting: Ref<boolean> = ref(false);

    // ── Computed ──────────────────────────────────────────────────────────

    const statusText: ComputedRef<string> = computed(() => {
        const p = phase.value;

        if (p.stage === "setup") {
            const textMap = isReconnecting.value ? CONFIG.reconnectSetupText : CONFIG.setupText;
            if (p.step === "replaying_history" && p.detail) {
                return `Restoring your conversation (${p.detail})…`;
            }
            return textMap[p.step] ?? "";
        }

        if (p.stage === "active") {
            if (p.phase === "listening") {
                return showListeningText.value ? CONFIG.activeText.listening : "";
            }
            if (p.phase === "thinking") {
                switch (thinkingEscalation.value) {
                    case "slow":
                        return CONFIG.degradedText.slowThinking;
                    case "very_slow":
                        return CONFIG.degradedText.verySlowThinking;
                    default:
                        return CONFIG.activeText.thinking;
                }
            }
            if (p.phase === "interrupted") {
                // The interruption is a transient state — no separate status text
                return "";
            }
            return CONFIG.activeText[p.phase] ?? "";
        }

        if (p.stage === "muted") {
            return "";
        }
        if (p.stage === "reconnecting") {
            if (p.attempts > 1) {
                return CONFIG.reconnectionText.tryingWithAttempt.replace("{n}", String(p.attempts));
            }
            return CONFIG.reconnectionText.trying;
        }
        if (p.stage === "reconnect_failed") {
            return CONFIG.reconnectionText.gaveUp;
        }
        if (p.stage === "error") {
            return p.message;
        }
        return "";
    });

    const marbleState: ComputedRef<MarbleState> = computed(() => {
        const p = phase.value;
        if (p.stage === "setup") {
            return "setup";
        }
        if (p.stage === "muted") {
            return "muted";
        }
        if (p.stage === "reconnecting") {
            return "reconnecting";
        }
        if (p.stage === "reconnect_failed") {
            return "muted";
        }
        if (p.stage === "active") {
            switch (p.phase) {
                case "speaking":
                    return "speaking";
                case "user_speaking":
                case "interrupted":
                    return "user_speaking";
                case "processing_turn":
                case "thinking":
                    return "thinking";
                case "listening":
                default:
                    return "listening";
            }
        }
        return "listening";
    });

    const isSetup: ComputedRef<boolean> = computed(() => phase.value.stage === "setup");
    const isActive: ComputedRef<boolean> = computed(() => phase.value.stage === "active");

    // ── Timer helpers ────────────────────────────────────────────────────

    function clearTranscriptFadeTimers() {
        if (assistantFadeTimer) {
            clearTimeout(assistantFadeTimer);
            assistantFadeTimer = null;
        }
        if (userFadeTimer) {
            clearTimeout(userFadeTimer);
            userFadeTimer = null;
        }
    }

    function fadeOnInterruption() {
        transcripts.value.user.interrupted = true;
        transcripts.value.assistant.interrupted = true;
        setTimeout(() => {
            transcripts.value.user = { text: "", final: false, visible: false, interrupted: false };
            transcripts.value.assistant = { text: "", final: false, visible: false, interrupted: false };
        }, CONFIG.timing.interruptionAckMs);
    }

    function clearThinkingTimers() {
        if (slowThinkingTimer) {
            clearTimeout(slowThinkingTimer);
            slowThinkingTimer = null;
        }
        if (verySlowThinkingTimer) {
            clearTimeout(verySlowThinkingTimer);
            verySlowThinkingTimer = null;
        }
        thinkingEscalation.value = "normal";
    }

    // ── Event handler — called for every WS message ─────────────────────

    function onEvent(event: ProtocolEvent): { audioDelta?: string; responseId?: string; maxDuration?: boolean } {
        switch (event.type) {
            case "setup_status":
                phase.value = { stage: "setup", step: event.step, detail: event.detail };
                break;

            case "session_state": {
                // Track previous phase for interruption detection
                const prevPhase = phase.value;
                const wasSpeaking = prevPhase.stage === "active" && prevPhase.phase === "speaking";

                phase.value = { stage: "active", phase: event.state };

                switch (event.state) {
                    case "speaking":
                        showListeningText.value = false;
                        if (listeningFadeTimer) {
                            clearTimeout(listeningFadeTimer);
                        }
                        clearThinkingTimers();
                        break;

                    case "listening":
                        // Nadia finished: transcripts stay visible until next user turn.
                        showListeningText.value = true;
                        if (listeningFadeTimer) {
                            clearTimeout(listeningFadeTimer);
                        }
                        listeningFadeTimer = setTimeout(() => {
                            showListeningText.value = false;
                        }, CONFIG.timing.listeningTextFadeMs);
                        clearThinkingTimers();
                        break;

                    case "user_speaking":
                        // New user turn — clear both transcripts.
                        // On barge-in, dim the assistant transcript briefly before clearing.
                        if (wasSpeaking) {
                            fadeOnInterruption();
                        } else {
                            transcripts.value.user = { text: "", final: false, visible: false, interrupted: false };
                            transcripts.value.assistant = { text: "", final: false, visible: false, interrupted: false };
                        }
                        clearTranscriptFadeTimers();
                        showListeningText.value = false;
                        if (listeningFadeTimer) {
                            clearTimeout(listeningFadeTimer);
                        }
                        clearThinkingTimers();
                        break;

                    case "processing_turn":
                        break;

                    case "thinking":
                        // Model is generating — start escalation timers.
                        thinkingEscalation.value = "normal";
                        slowThinkingTimer = setTimeout(() => {
                            thinkingEscalation.value = "slow";
                        }, CONFIG.timing.slowThinkingThresholdMs);
                        verySlowThinkingTimer = setTimeout(() => {
                            thinkingEscalation.value = "very_slow";
                        }, CONFIG.timing.verySlowThinkingThresholdMs);
                        break;

                    case "interrupted":
                        // OpenAI confirmed truncation via response.output_item.truncated.
                        // No additional visual change needed — the dim treatment was
                        // already applied when user_speaking fired (the barge-in trigger).
                        // We keep this as a distinct state for future telemetry/debugging.
                        // when user_speaking fired.
                        break;
                }
                break;
            }

            case "audio_delta":
                return { audioDelta: event.delta, responseId: event.response_id };

            case "transcript":
                if (event.role === "assistant") {
                    if (event.final) {
                        transcripts.value.assistant = { text: event.text, final: true, visible: true, interrupted: false };
                    }
                    // Non-final deltas are not displayed — only the final transcript is shown.
                } else {
                    if (event.final) {
                        transcripts.value.user = { text: event.text, final: true, visible: true, interrupted: false };
                    }
                    // Non-final deltas are not displayed — only the final transcript is shown.
                }
                break;

            case "session_reconnected":
                // Will be followed by setup_status "starting" or session_state events
                break;

            case "max_session_duration_reached":
                phase.value = { stage: "ended", reason: "max_duration" };
                clearTranscriptFadeTimers();
                clearThinkingTimers();
                return { maxDuration: true };

            case "error":
                phase.value = { stage: "error", message: event.message };
                clearThinkingTimers();
                break;

            case "settings_updated": {
                // Server confirmed the settings change — update local state
                const s = event.settings;
                if (s.pace) {
                    currentSettings.value.pace = s.pace;
                }
                if (s.microphone) {
                    currentSettings.value.microphone = s.microphone;
                }
                if (s.allowInterruptions !== undefined) {
                    currentSettings.value.allowInterruptions = s.allowInterruptions;
                }
                break;
            }
        }
        return {};
    }

    // ── External state setters (for events not from the WS protocol) ────

    function setMuted(muted: boolean) {
        if (muted) {
            phase.value = { stage: "muted" };
            clearThinkingTimers();
        } else {
            // Resume to listening — Nadia will respond when user speaks
            phase.value = { stage: "active", phase: "listening" };
        }
    }

    function setReconnecting(attempts: number) {
        // Transcripts are frozen (dimmed at 40% opacity) during reconnection so the user
        // sees continuity. TranscriptOverlay dims via the `frozen` prop when stage === 'reconnecting'.
        isReconnecting.value = true;
        phase.value = { stage: "reconnecting", attempts };
        clearThinkingTimers();
    }

    function setReconnectFailed() {
        phase.value = { stage: "reconnect_failed" };
        // Clear transcripts since the session is over
        transcripts.value = {
            assistant: makeEmptyTranscript(),
            user: makeEmptyTranscript(),
        };
        clearTranscriptFadeTimers();
        clearThinkingTimers();
    }

    function setEnded(reason: string) {
        phase.value = { stage: "ended", reason };
        clearTranscriptFadeTimers();
        clearThinkingTimers();
    }

    /**
     * Reset for a new connection. If `preserveTranscripts` is true (reconnection),
     * the last transcript state is kept. Otherwise (fresh start), transcripts are cleared.
     */
    function reset(preserveTranscripts = false) {
        phase.value = { stage: "setup", step: "connecting" };
        if (!preserveTranscripts) {
            transcripts.value = {
                assistant: makeEmptyTranscript(),
                user: makeEmptyTranscript(),
            };
            isReconnecting.value = false;
        }
        // isReconnecting stays true if preserveTranscripts is true (set by setReconnecting)
        showListeningText.value = false;

        clearTranscriptFadeTimers();
        clearThinkingTimers();
    }

    return {
        // State
        phase,
        transcripts,
        statusText,
        marbleState,
        isSetup,
        isActive,
        isReconnecting,
        thinkingEscalation,
        currentSettings,

        // Event handler
        onEvent,

        // External setters
        setMuted,
        setReconnecting,
        setReconnectFailed,
        setEnded,
        reset,
    };
}
